import React, { useEffect, useState } from 'react';
import { TableCell, TableRow, Fab, IconButton, useTheme, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { motion, AnimatePresence } from 'framer-motion';
import TableView from "./util/TableView";
import ProjectAssignmentDetailView from "./dialog/ProjectAssignmentDetailView";
import Moment from "react-moment";
import { ModuleHeader } from "../../components/composed";
import { Badge } from "../../components/primitives";
import { FadeIn } from "../../components/motion";
import { api } from "../api/api";
import { ProjectAssignment } from "../api/models";

export default function ProjectAssignmentsView(props: any) {
  const theme = useTheme();
  const [data, setData] = useState<ProjectAssignment[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<ProjectAssignment | null>(null);

  useEffect(() => {
    props.setShowSearch(true);
  }, []);

  const handleEdit = (row: ProjectAssignment) => {
    setSelected(row);
    setDialogOpen(true);
  };

  const handleDeactivate = (id: number) => {
    api.projectAssignment.deactivate(id)
      .then(() => {
        setData(prev => prev.map(a => a.id === id ? { ...a, active: false } : a));
      })
      .catch(props.onFetchFailed);
  };

  const tableHeader = (
    <TableRow>
      <TableCell>Person</TableCell>
      <TableCell>Project Name</TableCell>
      <TableCell>Start Date</TableCell>
      <TableCell>Managers</TableCell>
      <TableCell>HRs</TableCell>
      <TableCell>Active</TableCell>
      <TableCell>Actions</TableCell>
    </TableRow>
  );

  const tableRows = (row: ProjectAssignment) => (
    <TableRow key={row.id}>
      <TableCell>{row.person.name}</TableCell>
      <TableCell>{row.projectName}</TableCell>
      <TableCell>
        {row.startDate && <Moment date={row.startDate} format="DD/MM/YYYY" />}
      </TableCell>
      <TableCell>{row.managers.map(m => m.name).join(', ')}</TableCell>
      <TableCell>{row.hrs.map(h => h.name).join(', ')}</TableCell>
      <TableCell>
        <AnimatePresence mode="wait">
          <motion.div
            key={row.active ? 'active' : 'inactive'}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            style={{ display: 'inline-block' }}
          >
            <Badge variant={row.active ? 'success' : 'neutral'} size="small">
              {row.active ? 'Active' : 'Inactive'}
            </Badge>
          </motion.div>
        </AnimatePresence>
      </TableCell>
      <TableCell>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => handleEdit(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {row.active && (
          <Tooltip title="Deactivate">
            <IconButton size="small" onClick={() => handleDeactivate(row.id!)}>
              <BlockIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <FadeIn>
      <ModuleHeader
        title="Project Assignments"
        subtitle="Manage employee project assignments and anniversary notifications"
        moduleColor={theme.palette.primary.main}
      />
      <ProjectAssignmentDetailView
        open={dialogOpen}
        handleClose={() => { setDialogOpen(false); setSelected(null); }}
        assignment={selected}
        onSaved={(saved: ProjectAssignment) => {
          setData(prev => {
            const idx = prev.findIndex(a => a.id === saved.id);
            if (idx >= 0) {
              const updated = [...prev];
              updated[idx] = saved;
              return updated;
            }
            return [saved, ...prev];
          });
          setDialogOpen(false);
          setSelected(null);
        }}
        {...props}
      />
      <TableView
        fetchUrl="project-assignment"
        tableHeader={tableHeader}
        tableRows={tableRows}
        searchValue={props.searchValue}
        data={data}
        setData={setData}
        {...props}
      />
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.4 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        style={{
          position: 'fixed',
          bottom: theme.spacing(2),
          right: theme.spacing(2),
        }}
      >
        <Fab
          color="secondary"
          aria-label="add"
          onClick={() => { setSelected(null); setDialogOpen(true); }}
        >
          <AddIcon />
        </Fab>
      </motion.div>
    </FadeIn>
  );
}
